import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    ArrowLeft, 
    Mail, 
    Phone, 
    MapPin, 
    Shield, 
    Calendar,
    Clock,
    Award,
    AlertTriangle,
    Edit,
    Trash2,
    Plus,
    User
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
    { value: 'local_controller', label: 'Local Controller' },
    { value: 'deputy_local_controller', label: 'Deputy Local Controller' },
    { value: 'unit_duty_officer', label: 'Unit Duty Officer' },
    { value: 'group_leader', label: 'Group Leader' },
    { value: 'deputy_group_leader', label: 'Deputy Group Leader' },
    { value: 'general_member', label: 'General Member' },
];

const roleColors = {
    local_controller: 'bg-primary text-white',
    deputy_local_controller: 'bg-primary/80 text-white',
    unit_duty_officer: 'bg-blue-600 text-white',
    group_leader: 'bg-purple-600 text-white',
    deputy_group_leader: 'bg-purple-500 text-white',
    general_member: 'bg-secondary text-white',
};

const qualStatusColors = {
    current: 'status-available',
    expiring_soon: 'status-warning',
    expired: 'status-unavailable'
};

export default function MemberDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, hasUnitAccess } = useAuth();
    const [member, setMember] = useState(null);
    const [qualifications, setQualifications] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [editData, setEditData] = useState({});
    const [showAddQual, setShowAddQual] = useState(false);
    const [newQual, setNewQual] = useState({
        name: '',
        issuing_body: '',
        issue_date: '',
        expiry_date: ''
    });
    
    const isOwnProfile = user?.id === id;
    const canEdit = hasUnitAccess() || isOwnProfile;
    
    useEffect(() => {
        fetchMemberData();
    }, [id]);
    
    const fetchMemberData = async () => {
        try {
            const [memberRes, qualsRes, attendanceRes] = await Promise.all([
                axios.get(`${API_URL}/users/${id}`),
                axios.get(`${API_URL}/qualifications?user_id=${id}`),
                axios.get(`${API_URL}/attendance?user_id=${id}`)
            ]);
            setMember(memberRes.data);
            setEditData(memberRes.data);
            setQualifications(qualsRes.data);
            setAttendance(attendanceRes.data.slice(0, 20));
        } catch (error) {
            console.error('Failed to fetch member:', error);
            toast.error('Failed to load member details');
            navigate('/members');
        } finally {
            setLoading(false);
        }
    };
    
    const handleSaveEdit = async () => {
        try {
            const response = await axios.put(`${API_URL}/users/${id}`, editData);
            setMember(response.data);
            setEditMode(false);
            toast.success('Profile updated successfully');
        } catch (error) {
            toast.error('Failed to update profile');
        }
    };
    
    const handleDelete = async () => {
        try {
            await axios.delete(`${API_URL}/users/${id}`);
            toast.success('Member deleted');
            navigate('/members');
        } catch (error) {
            toast.error('Failed to delete member');
        }
    };
    
    const handleAddQualification = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/qualifications`, {
                user_id: id,
                ...newQual
            });
            toast.success('Qualification added');
            setShowAddQual(false);
            setNewQual({ name: '', issuing_body: '', issue_date: '', expiry_date: '' });
            fetchMemberData();
        } catch (error) {
            toast.error('Failed to add qualification');
        }
    };
    
    const handleDeleteQualification = async (qualId) => {
        try {
            await axios.delete(`${API_URL}/qualifications/${qualId}`);
            toast.success('Qualification removed');
            fetchMemberData();
        } catch (error) {
            toast.error('Failed to remove qualification');
        }
    };
    
    if (loading) {
        return (
            <div className="space-y-6">
                <div className="skeleton h-8 w-32 rounded"></div>
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-6">
                            <div className="skeleton w-24 h-24 rounded-full"></div>
                            <div className="space-y-2">
                                <div className="skeleton h-6 w-48 rounded"></div>
                                <div className="skeleton h-4 w-32 rounded"></div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }
    
    if (!member) return null;
    
    return (
        <div className="space-y-6 animate-fade-in" data-testid="member-detail-page">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/members')} data-testid="back-btn">
                    <ArrowLeft className="w-5 h-5" />
                </Button>
                <h2 className="text-2xl font-bold">Member Profile</h2>
            </div>
            
            {/* Profile Card */}
            <Card>
                <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start gap-6">
                        <Avatar className="h-24 w-24">
                            <AvatarImage src={member.profile_image} />
                            <AvatarFallback className="bg-primary text-white text-3xl">
                                {member.first_name?.[0]}{member.last_name?.[0]}
                            </AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-1">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                <div>
                                    <h1 className="text-2xl font-bold">
                                        {member.first_name} {member.last_name}
                                    </h1>
                                    <Badge className={`mt-2 ${roleColors[member.role]}`}>
                                        {member.role_name}
                                    </Badge>
                                </div>
                                
                                {canEdit && (
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setEditMode(true)}
                                            data-testid="edit-profile-btn"
                                        >
                                            <Edit className="w-4 h-4 mr-2" />
                                            Edit
                                        </Button>
                                        {hasUnitAccess() && !isOwnProfile && (
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button variant="destructive" size="sm" data-testid="delete-member-btn">
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Delete Member</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            Are you sure you want to delete {member.first_name} {member.last_name}? 
                                                            This action cannot be undone.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                        <AlertDialogAction onClick={handleDelete} className="bg-destructive">
                                                            Delete
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                                <div className="flex items-center gap-3 text-muted-foreground">
                                    <Mail className="w-5 h-5" />
                                    <span>{member.email}</span>
                                </div>
                                {member.phone && (
                                    <div className="flex items-center gap-3 text-muted-foreground">
                                        <Phone className="w-5 h-5" />
                                        <span>{member.phone}</span>
                                    </div>
                                )}
                                {member.address && (
                                    <div className="flex items-center gap-3 text-muted-foreground">
                                        <MapPin className="w-5 h-5" />
                                        <span>{member.address}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-3 text-muted-foreground">
                                    <Calendar className="w-5 h-5" />
                                    <span>Joined {format(new Date(member.created_at), 'MMM d, yyyy')}</span>
                                </div>
                            </div>
                            
                            {member.emergency_contact_name && (
                                <div className="mt-6 p-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900">
                                    <h4 className="font-medium text-red-800 dark:text-red-300 flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        Emergency Contact
                                    </h4>
                                    <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                                        {member.emergency_contact_name} - {member.emergency_contact_phone}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
            
            {/* Tabs */}
            <Tabs defaultValue="qualifications" className="w-full">
                <TabsList className="w-full sm:w-auto">
                    <TabsTrigger value="qualifications" className="flex-1 sm:flex-none" data-testid="tab-qualifications">
                        <Award className="w-4 h-4 mr-2" />
                        Qualifications
                    </TabsTrigger>
                    <TabsTrigger value="attendance" className="flex-1 sm:flex-none" data-testid="tab-attendance">
                        <Clock className="w-4 h-4 mr-2" />
                        Attendance
                    </TabsTrigger>
                </TabsList>
                
                {/* Qualifications Tab */}
                <TabsContent value="qualifications" className="mt-6">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Qualifications & Skills</CardTitle>
                                    <CardDescription>Training certifications and competencies</CardDescription>
                                </div>
                                {canEdit && (
                                    <Dialog open={showAddQual} onOpenChange={setShowAddQual}>
                                        <DialogTrigger asChild>
                                            <Button size="sm" className="ses-btn-primary gap-2" data-testid="add-qual-btn">
                                                <Plus className="w-4 h-4" />
                                                Add
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Add Qualification</DialogTitle>
                                                <DialogDescription>
                                                    Record a new qualification or certification
                                                </DialogDescription>
                                            </DialogHeader>
                                            <form onSubmit={handleAddQualification} className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label>Qualification Name</Label>
                                                    <Input
                                                        value={newQual.name}
                                                        onChange={(e) => setNewQual({ ...newQual, name: e.target.value })}
                                                        placeholder="e.g., Chainsaw Operator"
                                                        required
                                                        data-testid="qual-name"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Issuing Body</Label>
                                                    <Input
                                                        value={newQual.issuing_body}
                                                        onChange={(e) => setNewQual({ ...newQual, issuing_body: e.target.value })}
                                                        placeholder="e.g., QLD SES"
                                                        data-testid="qual-issuer"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Issue Date</Label>
                                                        <Input
                                                            type="date"
                                                            value={newQual.issue_date}
                                                            onChange={(e) => setNewQual({ ...newQual, issue_date: e.target.value })}
                                                            required
                                                            data-testid="qual-issue-date"
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Expiry Date</Label>
                                                        <Input
                                                            type="date"
                                                            value={newQual.expiry_date}
                                                            onChange={(e) => setNewQual({ ...newQual, expiry_date: e.target.value })}
                                                            data-testid="qual-expiry-date"
                                                        />
                                                    </div>
                                                </div>
                                                <DialogFooter>
                                                    <Button type="button" variant="outline" onClick={() => setShowAddQual(false)}>
                                                        Cancel
                                                    </Button>
                                                    <Button type="submit" className="ses-btn-primary" data-testid="submit-qual">
                                                        Add Qualification
                                                    </Button>
                                                </DialogFooter>
                                            </form>
                                        </DialogContent>
                                    </Dialog>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            {qualifications.length > 0 ? (
                                <div className="space-y-3">
                                    {qualifications.map((qual) => (
                                        <div
                                            key={qual.id}
                                            className="flex items-center justify-between p-4 rounded-lg border bg-card"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="p-2 rounded-lg bg-primary/10">
                                                    <Award className="w-5 h-5 text-primary" />
                                                </div>
                                                <div>
                                                    <h4 className="font-medium">{qual.name}</h4>
                                                    <p className="text-sm text-muted-foreground">
                                                        {qual.issuing_body && `${qual.issuing_body} • `}
                                                        Issued: {format(new Date(qual.issue_date), 'MMM d, yyyy')}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <Badge className={`status-badge ${qualStatusColors[qual.status]}`}>
                                                    {qual.status === 'current' && 'Current'}
                                                    {qual.status === 'expiring_soon' && 'Expiring Soon'}
                                                    {qual.status === 'expired' && 'Expired'}
                                                </Badge>
                                                {qual.expiry_date && (
                                                    <span className="text-xs text-muted-foreground">
                                                        Expires: {format(new Date(qual.expiry_date), 'MMM d, yyyy')}
                                                    </span>
                                                )}
                                                {canEdit && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="text-destructive hover:text-destructive"
                                                        onClick={() => handleDeleteQualification(qual.id)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Award className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>No qualifications recorded</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
                
                {/* Attendance Tab */}
                <TabsContent value="attendance" className="mt-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Attendance History</CardTitle>
                            <CardDescription>Recent clock in/out records</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {attendance.length > 0 ? (
                                <div className="space-y-3">
                                    {attendance.map((record) => (
                                        <div
                                            key={record.id}
                                            className="flex items-center justify-between p-4 rounded-lg border bg-card"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`p-2 rounded-lg ${record.clock_out ? 'bg-muted' : 'bg-green-100 dark:bg-green-900/30'}`}>
                                                    <Clock className={`w-5 h-5 ${record.clock_out ? 'text-muted-foreground' : 'text-green-600'}`} />
                                                </div>
                                                <div>
                                                    <h4 className="font-medium">
                                                        {record.activity_name || 'General Attendance'}
                                                    </h4>
                                                    <p className="text-sm text-muted-foreground">
                                                        {format(new Date(record.clock_in), 'MMM d, yyyy h:mm a')}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                {record.clock_out ? (
                                                    <>
                                                        <p className="font-medium">{record.duration_hours?.toFixed(1)} hrs</p>
                                                        <p className="text-xs text-muted-foreground">Completed</p>
                                                    </>
                                                ) : (
                                                    <Badge className="status-available">Active</Badge>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>No attendance records</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
            
            {/* Edit Dialog */}
            <Dialog open={editMode} onOpenChange={setEditMode}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit Profile</DialogTitle>
                        <DialogDescription>
                            Update member information
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>First Name</Label>
                                <Input
                                    value={editData.first_name || ''}
                                    onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                                    data-testid="edit-firstname"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Last Name</Label>
                                <Input
                                    value={editData.last_name || ''}
                                    onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                                    data-testid="edit-lastname"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input
                                type="email"
                                value={editData.email || ''}
                                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                                data-testid="edit-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Phone</Label>
                            <Input
                                value={editData.phone || ''}
                                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                                data-testid="edit-phone"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Address</Label>
                            <Input
                                value={editData.address || ''}
                                onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                                data-testid="edit-address"
                            />
                        </div>
                        {hasUnitAccess() && (
                            <div className="space-y-2">
                                <Label>Role</Label>
                                <Select
                                    value={editData.role}
                                    onValueChange={(value) => setEditData({ ...editData, role: value })}
                                >
                                    <SelectTrigger data-testid="edit-role">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ROLES.map((role) => (
                                            <SelectItem key={role.value} value={role.value}>
                                                {role.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="border-t pt-4 mt-4">
                            <h4 className="font-medium mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-red-500" />
                                Emergency Contact
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Contact Name</Label>
                                    <Input
                                        value={editData.emergency_contact_name || ''}
                                        onChange={(e) => setEditData({ ...editData, emergency_contact_name: e.target.value })}
                                        data-testid="edit-emergency-name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Contact Phone</Label>
                                    <Input
                                        value={editData.emergency_contact_phone || ''}
                                        onChange={(e) => setEditData({ ...editData, emergency_contact_phone: e.target.value })}
                                        data-testid="edit-emergency-phone"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                        <Button onClick={handleSaveEdit} className="ses-btn-primary" data-testid="save-edit-btn">
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
